import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/index.js';

/**
 * Site-wide license banner. Sits below the maintenance banner and:
 *   - hides itself when status is "active" with no expiry warnings
 *   - shows AMBER when status is `active_with_warning`, `time_unsynced`,
 *     or `days_until_maintenance < 30`
 *   - shows RED when any restrictive status is active OR
 *     `days_until_expiry < 30`
 *
 * Polls /api/license/status every 10s. The endpoint requires auth, so the
 * banner is only mounted under DashboardShell (logged-in side of the app).
 */

const POLL_MS = 10_000;

const RESTRICTIVE = new Set([
  'not_activated',
  'not_configured',
  'ipc_unreachable',
  'expired',
  'revoked',
  'hardware_mismatch',
  'software_uuid_mismatch',
  'signature_invalid',
  'tampering_detected',
  'unsupported_token_version',
  'unsupported_crypto_suite',
]);

// Friendly per-status copy. Keep messages short — banner is one line.
const STATUS_COPY = {
  not_activated:             'License is not activated. Some operations are blocked.',
  not_configured:            'License IPC secret is not configured. Some operations are blocked.',
  ipc_unreachable:           'Cannot reach the licensing component (ad-kms-service). Some operations are blocked.',
  expired:                   'License has expired. Operations are blocked until renewal.',
  revoked:                   'License has been revoked. Operations are blocked.',
  hardware_mismatch:         'Hardware configuration changed — license is no longer valid for this VM.',
  software_uuid_mismatch:    'License does not match this product. Check ADMS_LICENSE_UUID configuration.',
  signature_invalid:         'License token signed by an untrusted key. Contact MicroGIS support.',
  tampering_detected:        'Integrity check failed on the licensing component. Installation may be compromised.',
  unsupported_token_version: 'License token version is not supported by ad-kms-service. Update required.',
  unsupported_crypto_suite:  'License token uses an unsupported crypto suite. Update required.',
  active_with_warning:       'License is active with warnings.',
  time_unsynced:             'System clock is not synchronised. License operates in degraded mode.',
};

export function LicenseBanner() {
  const [license, setLicense] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer;

    async function tick() {
      try {
        const r = await api.license.status();
        if (!cancelled) setLicense(r.license);
      } catch {
        // Probably 401 — user signed out. Hide banner; outer layout will
        // unmount us when DashboardShell goes away.
      }
    }
    tick();
    timer = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (!license) return null;

  const status = license.status;

  // Don't render at all on a clean active license without expiry warnings.
  const expiringSoon  = typeof license.daysUntilExpiry      === 'number' && license.daysUntilExpiry      < 30;
  const maintEndingSoon = typeof license.daysUntilMaintenance === 'number' && license.daysUntilMaintenance < 30;
  const isWarning     = status === 'active_with_warning' || status === 'time_unsynced' || maintEndingSoon;
  const isRestricted  = RESTRICTIVE.has(status) || expiringSoon || status === 'expired';
  if (!isWarning && !isRestricted) return null;

  const tone = isRestricted ? 'restricted' : 'warning';
  const icon = isRestricted ? 'fa-circle-exclamation' : 'fa-triangle-exclamation';

  // Compose message — pick the most actionable copy. Restrictive status
  // beats expiring-soon (which is itself amber-but-still-permissive in some
  // statuses); explicit days_until_expiry < 30 always wins amber→red.
  let message = STATUS_COPY[status] || `License status: ${status}.`;
  if (expiringSoon && license.expiresAt) {
    message = `License expires in ${license.daysUntilExpiry} day${license.daysUntilExpiry === 1 ? '' : 's'} (${formatShort(license.expiresAt)}). ${STATUS_COPY[status] || ''}`.trim();
  } else if (maintEndingSoon && license.maintenanceUntil && !isRestricted) {
    message = `Maintenance ends in ${license.daysUntilMaintenance} day${license.daysUntilMaintenance === 1 ? '' : 's'} (${formatShort(license.maintenanceUntil)}). Contact your administrator about renewal.`;
  }

  return (
    <div
      role="status"
      className={
        tone === 'restricted'
          ? 'border-b border-rose-300 bg-rose-soft text-rose-800'
          : 'border-b border-amber-300 bg-amber-50 text-amber-800'
      }
    >
      <div className="mx-auto flex max-w-screen-2xl items-center gap-3 px-5 py-2 text-sm">
        <i className={`fa-solid ${icon} shrink-0`} aria-hidden="true" />
        <p className="flex-1 leading-snug">{message}</p>
        <Link
          to="/settings#license"
          className={
            'shrink-0 rounded-lg border px-3 py-1 text-xs font-semibold transition-colors ' +
            (tone === 'restricted'
              ? 'border-rose-400 bg-white/60 hover:bg-white'
              : 'border-amber-400 bg-white/70 hover:bg-white')
          }
        >
          Open Settings
        </Link>
      </div>
    </div>
  );
}

function formatShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
